/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext } from "react";
import DataTable, { Column, Row } from "components/DataTable";
import { formatDuration } from "pages/employee/WorkDetails";
import { convertUtcToIst } from "../../util/Util"; // Adjust path as necessary
import { BannerContext } from "components/SideBar";

interface SessionData {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  duration: number | string;
  productivity: string;
  application: string;
  status?: string;
}

interface SessionProps {
  sessions: SessionData[];
  totalPages?: number;
  totalElements?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  disabledBanner?: React.ReactNode;
}

const Sessions: React.FC<SessionProps> = ({
  disabledBanner,
  sessions,
  totalPages = 0,
  totalElements = 0,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
}) => {
  // Define columns for the DataTable
  const columns: Column[] = [
    {
      key: "date",
      label: "Date",
      render: (value, row: any) => {
        // Value is already normalized to UTC in the tableData mapping
        const startTime = row.startTime || row.createdAt;
        const formatted = convertUtcToIst(startTime);
        return (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <span className="text-gray-900 dark:text-white font-medium">{formatted.date}</span>
          </div>
        );
      },
    },
    {
      key: "application",
      label: "Application",
      render: (value) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-4 h-4 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <span className="text-gray-900 dark:text-white">{value || "N/A"}</span>
        </div>
      ),
    },
    {
      key: "startTime",
      label: "Start Time",
      render: (value, row: any) => {
        const isoTime = row.startTime || value;
        const formatted = convertUtcToIst(isoTime);
        return (
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
            <svg
              className="w-4 h-4 text-green-500 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{formatted.time}</span>
          </div>
        );
      },
    },
    {
      key: "endTime",
      label: "End Time",
      render: (value, row: any) => {
        const isoTime = row.endTime || value;
        const formatted = convertUtcToIst(isoTime);
        return (
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
            <svg
              className="w-4 h-4 text-red-500 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{formatted.time}</span>
          </div>
        );
      },
    },
    {
      key: "duration",
      label: "Duration",
      render: (value) => {
        const durationValue =
          typeof value === "number" || typeof value === "string" ? value : 0;
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
            ⏱️ {formatDuration(durationValue)}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Session Type",
      render: (value) => {
        const isActive = value === "ACTIVE";
        return (
          <div className="flex items-center gap-2">
            {isActive ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700">
                  <span className="w-2 h-2 mr-1.5 rounded-full bg-green-500 dark:bg-green-400 animate-pulse"></span>{" "}
                  ACTIVE
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 text-red-700 dark:text-red-300 border border-gray-200 dark:border-gray-600">
                  <span className="w-2 h-2 mr-1.5 rounded-full bg-red-700 dark:bg-red-400"></span>{" "}
                  IDLE
                </span>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const { isBannerVisible } = useContext(BannerContext);
              const dynamicHeightClass = isBannerVisible
                ? "h-[calc(100vh-390px)] min-h-[300px]"
                : "h-[calc(100vh-350px)] min-h-[300px]";

  const ensureUtc = (timeStr: any) => {
    if (typeof timeStr === "string" && !timeStr.endsWith("Z")) {
      return `${timeStr}Z`;
    }
    return timeStr;
  };

  const tableData: Row[] = sessions.map((session) => ({
    id: session.id,
    date: ensureUtc(session.startTime || session.date),
    application: session.application,
    startTime: ensureUtc(session.startTime),
    endTime: ensureUtc(session.endTime),
    duration: session.duration,
    productivity: session.productivity,
    status: session.status || "IDLE",
  }));

  return (
    <div className="space-y-4 px-2">
      <DataTable
        data={tableData}
        columns={columns}
        loading={false}
        isExportable={true}
        isShowFilter={false}
        hidePagination={true}
        itemsPerPage={tableData.length || 10}
        centerToolbarElement={disabledBanner}
        heightClass={dynamicHeightClass} // <-- Sets custom dynamic height to prevent outer double-scroll
      />

      {/* Server Pagination Controls */}
      {totalElements > 0 && onPageChange && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Showing{" "}
              {Math.min((currentPage - 1) * pageSize + 1, totalElements)} -{" "}
              {Math.min(currentPage * pageSize, totalElements)} of{" "}
              {totalElements} entries
            </span>
            <select
              value={pageSize}
              onChange={(e) => onPageChange(1, Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
              <option value={100}>100 rows</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1, pageSize)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum, pageSize)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${currentPage === pageNum
                      ? "bg-blue-600 dark:bg-blue-700 text-white"
                      : "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600"
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => onPageChange(currentPage + 1, pageSize)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-300 ml-2">
              Page {currentPage} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
export default Sessions;